import React, { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { useTranslation } from 'react-i18next';
import { RTLContext } from './RTLContext';
import type { RTLContextType } from './RTLContext';
import { storageService, STORAGE_KEYS } from '@/services/storageService';

interface RTLProviderProps {
  children: ReactNode;
  defaultDirection?: 'ltr' | 'rtl';
}

// Create RTL cache
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

// Create LTR cache
const cacheLtr = createCache({
  key: 'muiltr',
  stylisPlugins: [prefixer],
});

export const RTLProvider: React.FC<RTLProviderProps> = ({
  children,
  defaultDirection = 'rtl',
}) => {
  const { i18n } = useTranslation();

  const [direction, setDirectionState] = useState<'ltr' | 'rtl'>(() => {
    // Determine direction based on current language
    const currentLang = i18n.language || i18n.options.fallbackLng;
    const isRTL = currentLang === 'he' || currentLang === 'sy'; // Hebrew and Syrian are RTL

    // Check localStorage for saved direction preference, but prefer language-based direction
    const savedDirection = storageService.getItem<'ltr' | 'rtl'>(
      STORAGE_KEYS.TEXT_DIRECTION
    );

    // If there's a saved direction and it matches the language direction, use it
    // Otherwise, use language-based direction
    if (
      savedDirection &&
      (savedDirection === 'ltr' || savedDirection === 'rtl')
    ) {
      return isRTL ? 'rtl' : savedDirection;
    }

    return isRTL ? 'rtl' : defaultDirection;
  });

  const setDirection = (newDirection: 'ltr' | 'rtl') => {
    setDirectionState(newDirection);
    storageService.setItem(STORAGE_KEYS.TEXT_DIRECTION, newDirection);
    // Update document direction
    document.documentElement.dir = newDirection;
    // Keep current language
    const currentLang = i18n.language || 'he';
    document.documentElement.lang = currentLang;
  };

  const toggleDirection = () => {
    const newDirection = direction === 'ltr' ? 'rtl' : 'ltr';
    setDirection(newDirection);
  };

  // Listen for language changes and update direction automatically
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const shouldBeRTL = lng === 'he' || lng === 'sy';
      const newDirection = shouldBeRTL ? 'rtl' : 'ltr';

      if (direction !== newDirection) {
        setDirection(newDirection);
      }
    };

    // Subscribe to language changes
    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [direction, i18n]);

  // Set initial document direction and language
  useEffect(() => {
    document.documentElement.dir = direction;
    // Set language attribute based on current i18n language
    const currentLang = i18n.language || 'he';
    document.documentElement.lang = currentLang;
  }, [direction, i18n.language]);

  const contextValue: RTLContextType = {
    direction,
    toggleDirection,
    setDirection,
  };

  return (
    <RTLContext.Provider value={contextValue}>
      <CacheProvider value={direction === 'rtl' ? cacheRtl : cacheLtr}>
        {children}
      </CacheProvider>
    </RTLContext.Provider>
  );
};
